import isNil from 'lodash/isNil'
import * as React from 'react'
import ReactDOM from 'react-dom'
import { Manager, Popper, PopperProps, Reference } from 'react-popper'
import { CSSTransition } from 'react-transition-group'
import styled from 'styled-components'
import { throttle } from 'throttle-debounce'
import { stripUnit, zIndex } from 'utils'
import uuidv4 from 'uuid/v4'

import theme from '../theme'
import { fastFade } from '../Transition'
import Content from './Content'

const EyeCandy = styled.div`
  ${fastFade};
  z-index: ${zIndex(4)};
`

export type RenderTriggerType = (
  e2e: string,
  onClick: () => void,
  active: boolean,
  ref: React.Ref<any>,
  className: string
) => React.ReactNode

type PropsType = {
  active?: boolean
  children: React.ReactNode
  e2e: string
  hideOnScroll?: boolean
  modifiers?: Pick<PopperProps, 'modifiers'>
  onVisibleToggle?: () => void
  placement?: 'top' | 'right' | 'bottom' | 'left'
  renderTrigger: RenderTriggerType
}

type StateType = {
  active: boolean
}

class Popover extends React.Component<PropsType, StateType> {
  static defaultProps = {
    placement: 'bottom-start'
  }

  container: HTMLElement | null = null

  id = uuidv4()

  throttledHandleScroll: () => void

  constructor(props: PropsType) {
    super(props)

    this.container = document.getElementById('popover')
    this.throttledHandleScroll = throttle(50, this.handleScroll)
  }

  state = {
    active: false
  }

  componentDidMount() {
    if (isNil(this.props.hideOnScroll) || this.props.hideOnScroll === true) {
      document.addEventListener('scroll', this.throttledHandleScroll, true)
    }
  }

  componentWillUnmount() {
    if (isNil(this.props.hideOnScroll) || this.props.hideOnScroll === true) {
      document.removeEventListener('scroll', this.throttledHandleScroll, true)
    }
  }

  handleClickOutside = () => {
    this.setState({ active: false })

    if (this.props.onVisibleToggle && this.props.active === true) {
      this.props.onVisibleToggle()
    }
  }

  handleButtonClick = () => {
    this.setState((prevState) => ({ active: !prevState.active }))

    if (this.props.onVisibleToggle) {
      this.props.onVisibleToggle()
    }
  }

  handleScroll = () => {
    if (this.state.active) {
      this.handleClickOutside()
    }
  }

  render() {
    const { container, handleButtonClick, handleClickOutside, id } = this
    const { children, e2e, modifiers, placement, renderTrigger } = this.props
    const isActive = isNil(this.props.active) ? this.state.active : this.props.active

    return (
      <Manager>
        <Reference>
          {({ ref }) => renderTrigger(e2e, handleButtonClick, isActive, ref, id)}
        </Reference>
        <Popper modifiers={modifiers} placement={placement}>
          {({ ref, style }) => (
            <CSSTransition
              classNames='fade'
              in={isActive}
              timeout={stripUnit(theme.transition.fast)}
              unmountOnExit
            >
              <>
                {container &&
                  ReactDOM.createPortal(
                    <EyeCandy ref={ref} style={style}>
                      <Content onClickOutside={handleClickOutside} outsideClickIgnoreClass={id}>
                        {children}
                      </Content>
                    </EyeCandy>,
                    container
                  )}
              </>
            </CSSTransition>
          )}
        </Popper>
      </Manager>
    )
  }
}

export * from './styles'

export default Popover
